<template>
  <div class="no-overflow-modal">
    <div v-if="positionData && form" class="no-overflow-modal">
      <div class="columns">
        <div dir="ltr" class="column b-r has-text-left">
          <div class="box">

            <div class="is-flex is-justify-content-space-between">
              <span class="label">Company:</span>
              <span>{{ positionData.company.name }}</span>
            </div>

            <div class="field mt-3">
              <Title :positionId="positionData.id" v-model="form.data.position.fields.title.value" />
            </div>
            <div class="field mt-4">
              <ContactEmail :positionId="positionData.id" v-model="contactEmail" />
            </div>
            <div class="box mt-4">
              <JobTitles :position="positionData" />
            </div>

            <div class="my-4">
              <button @mouseover="openSkillsModal" @mouseleave="removeSkillsModal" class="button is-info is-fullwidth">Skills</button>
            </div>
            <div class="field mt-4">
              <WorkEntails :positionId="positionData.id" v-model="workEntails" />
            </div>
            <div class="field mt-4">
              <Abstract :positionId="positionData.id" v-model="form.data.position.fields.abstract.value" />
            </div>
            <div class="field mt-4">
              <PriorityWarning :positionId="positionData.id" v-model="form.data.position.fields.priority_warning.value" />
            </div>
            <div class="field">
              <MaxSalaryExpectations :positionId="positionData.id" v-model="form.data.position.fields.max_salary_expectations.value" />
            </div>
            <div class="field mt-4">
              <SalaryExpectationsComment :positionId="positionData.id" v-model="form.data.position.fields.salary_expectations_comment.value" />
            </div>
            <div class="mt-3">
              <LocationEditor :onSubmit="handleLocation" :payload="{ location: location, resource_type: 'Position', resource_id: positionData.id }" />
            </div>
            <div class="mt-3">
              <Owner :positionId="positionData.id" v-model="form.data.position.fields.owner.value" />
            </div>
            <div class="field mt-2">
              <Message :positionId="positionData.id" v-model="form.data.position.fields.message.value" />
            </div>
            <div class="field mt-2">
              <IsDiscrete :positionId="positionData.id" v-model="form.data.position.fields.is_discrete.value" />
            </div>
            <div class="mt-3">
              <Status :positionId="positionData.id" v-model="form.data.position.fields.status.value" />
            </div>
          </div>
        </div>
        <div class="column b-r" style="overflow: hidden;">
          <div class="box">
            <div class="field mb-3">
              <!-- who last changed the raw text, and when — same muted provenance style as the applicant Abstract -->
              <div class="is-flex is-align-items-baseline mb-2">
                <label class="label m-0">Company Raw Text</label>
                <span v-if="rawTextUpdatedLabel" class="is-size-7 has-text-grey-light ml-2 raw-text-updated" dir="rtl">{{ rawTextUpdatedLabel }}</span>
              </div>
              <div class="control">
                <textarea
                  class="textarea is-fullwidth"
                  placeholder="..."
                  rows="20"
                  v-model="form.data.position.fields.company_raw_text.value"
                  @focusout="handleFocusOut"
                />
              </div>
            </div>

            <!-- WhatsApp Templates Section -->
            <WhatsappTemplates
              v-model="whatsappTemplates"
              @input="handleWhatsappTemplatesChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex'

import LocationEditor from '@/components/LocationEditor'
import JobTitles from '@/components/entities/position/JobTitles'
import PriorityWarning from '@/components/entities/position/PriorityWarning'
import SalaryExpectationsComment from '@/components/entities/position/SalaryExpectationsComment'
import MaxSalaryExpectations from '@/components/entities/position/MaxSalaryExpectations'
import Abstract from '@/components/entities/position/Abstract'
import Title from '@/components/entities/position/Title'
import IsDiscrete from '@/components/entities/position/IsDiscrete'
import Message from '@/components/entities/position/Message'
import Owner from '@/components/entities/position/Owner'
import Status from '@/components/entities/position/Status'
import WorkEntails from '@/components/entities/position/WorkEntails'
import ContactEmail from '@/components/entities/position/ContactEmail'
import WhatsappTemplates from '@/components/entities/position/WhatsappTemplates'
import rawTextAuditMixin from '@/mixins/rawTextAuditMixin.js'

export default {
  name: 'ModalPositionAdvanced',
  mixins: [rawTextAuditMixin],
  components: {
    LocationEditor,
    PriorityWarning,
    JobTitles,
    SalaryExpectationsComment,
    MaxSalaryExpectations,
    Abstract,
    Title,
    IsDiscrete,
    Message,
    Owner,
    Status,
    WorkEntails,
    ContactEmail,
    WhatsappTemplates
  },
  props: {
    payload: {
      type: Object
    }
  },
  data () {
    return {
      positionData: undefined,
      abstract: undefined,
      form: undefined,
      location: undefined,
      workEntails: '',
      contactEmail: '',
      whatsappTemplates: {
        interest: '',
        infoRequest: '',
        conversationEnd: ''
      }
    }
  },
  computed: {
    ...mapGetters('modals', ['findModal'])
  },
  async created () {
    this.positionData = await this.$network_utils.getPosition({ id: this.payload.data.id })
    this.getPositionLocation()
    this.setForm(this.positionData)
    this.initRawTextAudit(this.positionData)
    this.getTexts()
    this.initializeWhatsappTemplates()
  },
  methods: {
    ...mapMutations('modals', ['create', 'remove']),
    setForm (form_data) {
      this.form = new this.$form({
        position: {
          fields: {
            title: {
              value: form_data.title || '',
              has_error: false,
              validations: []
            },
            abstract: {
              value: form_data.abstract || '',
              has_error: false,
              validations: []
            },
            status: {
              value: form_data.status,
              has_error: false,
              validations: []
            },
            owner: {
              value: form_data.owner || this.$utils.getOwner().name,
              has_error: false,
              validations: []
            },
            priority_warning: {
              value: form_data.priority_warning || '',
              has_error: false,
              validations: []
            },
            message: {
              value: form_data.message || '',
              has_error: false,
              validations: []
            },
            max_salary_expectations: {
              value: parseInt(form_data.max_salary_expectations),
              has_error: false,
              validations: ['Min', 'Max', 'Number'],
              min: 5000,
              max: 100000
            },
            salary_expectations_comment: {
              value: form_data.salary_expectations_comment || '',
              has_error: false,
              validations: []
            },
            company_raw_text: {
              value: form_data.company_raw_text,
              has_error: false,
              validations: []
            },
            is_discrete: {
              value: String(form_data.is_discrete),
              has_error: false,
              validations: []
            }
          },
          request_details: {
            method: 'put',
            url: `/positions/${this.positionData.id}`,
            headers: 'admin_headers'
          },
          is_used: true
        }
      })
    },
    handleLocation (data) {
      this.location = data
    },
    openSkillsModal () {
      this.create({
        designator: 'position-skills-modal',
        component_name: 'PositionSkills',
        position_id: this.positionData.id,
        company_id: this.positionData.company.id,
        position_data: this.positionData,
        modal_title: 'Position Skills'
      })
    },
    removeSkillsModal () {
      let modal = this.findModal({ type: 'designator', value: 'position-skills-modal' })
      this.remove(modal)
    },
    handleFocusOut () {
      if (!this.form.data.position.fields.max_salary_expectations.value) {
        this.$utils.emit('show_toast', {
          text: 'Max salary expectations must be set before updating position details, if unknown please set 99999',
          type: 'is-warning'
        })
        return
      }

      const token = this.beginRawTextSave(this.form.data.position.fields.company_raw_text.value)
      const response = this.form.mySubmit()
      if (response === false) {
        const errors = []
        Object.keys(this.form.data.position.fields).forEach(field => {
          if (this.form.data.position.fields[field].has_error) {
            errors.push(`${field.replace(/_/g, ' ')} is invalid`)
          }
        })

        this.$utils.emit('show_toast', {
          text: errors.join(', '),
          type: 'is-danger'
        })
        return
      }

      this.$utils.emit('show_toast', {
        text: 'Position updated successfully',
        type: 'is-success'
      })
      // Refresh "עודכן לאחרונה" once the save resolves; [0] is the position PUT.
      if (response && response.promise) {
        response.promise
          .then(results => this.finishRawTextSave(token, Array.isArray(results) ? results[0] : results))
          .catch(() => {})
      }
    },
    async getPositionLocation () {
      const [ location ] = await this.$network_utils.getResourceLocations({ current_type: 'Position', current_resource_id: this.positionData.id })
      this.location = location
    },
    async getTexts () {
      const texts = await this.$network_utils.getResourceTexts({ current_type: 'Position', current_resource_id: this.positionData.id })
      this.workEntails = (texts.find(text => text.designator == 'work_entails') || {}).content || ''
      this.contactEmail = (texts.find(text => text.designator == 'contact_email') || {}).content || ''
    },
    handleWhatsappTemplatesChange (templates) {
      this.whatsappTemplates = templates
      this.saveWhatsappTemplates()
    },
    async saveWhatsappTemplates () {
      try {
        await this.$network_utils.updatePosition({
          id: this.positionData.id,
          whatsapp_template_interest: this.whatsappTemplates.interest,
          whatsapp_template_info_request: this.whatsappTemplates.infoRequest,
          whatsapp_template_conversation_end: this.whatsappTemplates.conversationEnd
        })
      } catch (error) {
        console.error('Error saving WhatsApp templates:', error)
      }
    },
    initializeWhatsappTemplates () {
      this.whatsappTemplates = {
        interest: this.positionData.whatsapp_template_interest || '',
        infoRequest: this.positionData.whatsapp_template_info_request || '',
        conversationEnd: this.positionData.whatsapp_template_conversation_end || ''
      }
    }
  }
}
</script>
<style scoped>
  .b-r{
    height: 65vh;
    overflow-y: auto;
  }
</style>
